/**
 * Shared types for Document Delivery Rules
 *
 * Delivery rules determine when and how documents are delivered to team members.
 * They can be set-date based or triggered by employment lifecycle events.
 */
import { z } from "zod";
// ===== ENUM SCHEMAS =====
export const DocumentDeliveryTypeSchema = z.enum(["SET_DATE", "ACTION_BASED"]);
export const DocumentDeliveryTimingSchema = z.enum(["BEFORE", "AFTER", "ON"]);
export const DocumentDeliveryEventSchema = z.enum([
    "HIRE_DATE",
    "TERMINATION_DATE",
    "BIRTHDAY",
    "ANNIVERSARY",
    "UNAPPROVED_ABSENCE",
    "LATE_ARRIVAL",
]);
// ===== CONSTANTS =====
// Events that occur on a specific date (no offset applicable)
const EXACT_DATE_EVENTS = ["HIRE_DATE", "TERMINATION_DATE", "BIRTHDAY"];
// ===== INPUT SCHEMAS =====
export const SetDateDeliveryRuleInputSchema = z.object({
    deliveryType: z.literal("SET_DATE"),
    deliveryDate: z.iso.datetime(),
    isAnnual: z.boolean().optional().default(false),
});
export const ActionBasedDeliveryRuleInputSchema = z
    .object({
    deliveryType: z.literal("ACTION_BASED"),
    timing: DocumentDeliveryTimingSchema,
    triggerEvent: DocumentDeliveryEventSchema,
    offsetDays: z.number().int().nonnegative().optional(),
})
    .refine((data) => {
    if (EXACT_DATE_EVENTS.includes(data.triggerEvent)) {
        // Exact-date events must use "ON" timing and must not have offsetDays
        return data.timing === "ON" && data.offsetDays === undefined;
    }
    // Offset events allow any timing and may have offsetDays
    return true;
}, {
    message: "Events HIRE_DATE, TERMINATION_DATE, and BIRTHDAY require timing to be 'ON' and offsetDays must not be set",
});
export const DeliveryRuleInputSchema = z.discriminatedUnion("deliveryType", [
    SetDateDeliveryRuleInputSchema,
    ActionBasedDeliveryRuleInputSchema,
]);
// ===== RESPONSE SCHEMAS =====
export const DeliveryRuleResponseSchema = z.object({
    id: z.uuid(),
    deliveryType: DocumentDeliveryTypeSchema,
    deliveryDate: z.iso.datetime().nullable(),
    isAnnual: z.boolean(),
    timing: DocumentDeliveryTimingSchema.nullable(),
    offsetDays: z.number().int().nullable(),
    triggerEvent: DocumentDeliveryEventSchema.nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
